import React, { useEffect, useState } from 'react'
import * as API from "../../framework/API/api"
import { ENTITYNAME, LOGEVENT, MODULE, TOAST_TYPES } from '../../framework/constant/constant';
import { utility } from '../../framework/utility/utilityProvider'
import { useTranslation } from '../../locale/useTranslation'
import { EditPageHeader } from '../../components/EditPageHeader'
import BossDialog from '../../components/BossDialog'
import { DropDownList, MultiSelect } from '@progress/kendo-react-dropdowns'
import { Input } from '@progress/kendo-react-inputs'
import ToastNotification from '../../components/ToastNotification';

function SeasonEpisodeIdentifierSettingEditForm(props) {

    const lang = useTranslation();

    const blankDataItem = {
        ...props.item,
        Key: props.item.copy ? 'copy of ' + props.item.Key : props.item.Key ?? '',
        Value: props.item.Value ?? '',
        Archive: props.item.Archive ?? false,
    }

    const [channel, setChannel] = useState([]);
    const [dataItem, setDataItem] = useState(blankDataItem);
    const [formFieldData, setFormFieldData] = useState([]);
    const [currentInput, setCurrentInput] = useState('');
    const [currentEpisodeInput, setCurrentEpisodeInput] = useState('');

    useEffect(() => {
        loadcombo();
    }, []);

    const loadcombo = async () => {
        let channelRes = await API.getValidChannels();
        const channelsWithAll = [{ _id: 'all', FullChannelName: 'All Channels' }, ...channelRes.data];
        setChannel(channelsWithAll);

        let formFieldRes = await API.getData(ENTITYNAME.FormFields, {
            query: ["entityName", "=", 'mediaepisode'],
            sort: { sequence: 1 }
        });
        setFormFieldData(formFieldRes.data);

        // Set default to All Channels if no channels selected
        if (!dataItem.Channel || dataItem.Channel.length === 0) {
            setDataItem(prev => ({ ...prev, Channel: [{ _id: 'all', FullChannelName: 'All Channels' }] }));
        } else if (dataItem.Channel && dataItem.Channel.length > 0) {
            // If editing and channels exist, keep them as is
            const existingChannels = dataItem.Channel.map(ch => {
                if (typeof ch === 'string') {
                    // If channel is stored as ID string, find the full object
                    return channelsWithAll.find(c => c._id === ch) || ch;
                }
                return ch;
            });
            setDataItem(prev => ({ ...prev, Channel: existingChannels }));
        }
    }

    const onChange = (e) => {
        if (e.target.name == "Archive") {
            setDataItem({ ...dataItem, Archive: !dataItem.Archive });
        } else if (e.target.name == "Channels") {
            const selectedChannels = e.target.value;
            const allChannelItem = { _id: 'all', FullChannelName: 'All Channels' };
            const currentHasAll = dataItem.Channels && dataItem.Channels.some(ch => ch._id === 'all');
            const newHasAll = selectedChannels.some(ch => ch._id === 'all');
            const newHasSpecific = selectedChannels.some(ch => ch._id !== 'all');

            if (newHasAll && newHasSpecific) {
                // If both "All Channels" and specific channels are selected
                if (currentHasAll) {
                    // If "All Channels" was already selected, user is adding specific channels → remove "All Channels"
                    const filteredChannels = selectedChannels.filter(ch => ch._id !== 'all');
                    setDataItem({ ...dataItem, Channels: filteredChannels });
                } else {
                    // If specific channels were selected, user is adding "All Channels" → keep only "All Channels"
                    setDataItem({ ...dataItem, Channels: [allChannelItem] });
                }
            } else if (newHasAll && !newHasSpecific) {
                // If only "All Channels" is selected
                setDataItem({ ...dataItem, Channels: [allChannelItem] });
            } else if (selectedChannels.length === 0) {
                // If no channels selected, default to "All Channels"
                setDataItem({ ...dataItem, Channels: [allChannelItem] });
            } else {
                // If only specific channels are selected
                setDataItem({ ...dataItem, Channels: selectedChannels });
            }
        } else {
            setDataItem({ ...dataItem, [e.target.name]: e.target.value });
        }
    }

    const handleArrayInput = (e) => {
        if (e.key === 'Enter' && currentInput.trim()) {
            const newArray = dataItem.SeasonIdentifier ? [...dataItem.SeasonIdentifier, currentInput.trim()] : [currentInput.trim()];
            setDataItem({ ...dataItem, SeasonIdentifier: newArray });
            setCurrentInput('');
        }
    }

    const removeArrayItem = (index) => {
        const newArray = dataItem.SeasonIdentifier.filter((_, i) => i !== index);
        setDataItem({ ...dataItem, SeasonIdentifier: newArray });
    }

    const handleEpisodeArrayInput = (e) => {
        if (e.key === 'Enter' && currentEpisodeInput.trim()) {
            const newArray = dataItem.EpisodeIdentifier ? [...dataItem.EpisodeIdentifier, currentEpisodeInput.trim()] : [currentEpisodeInput.trim()];
            setDataItem({ ...dataItem, EpisodeIdentifier: newArray });
            setCurrentEpisodeInput('');
        }
    }

    const removeEpisodeArrayItem = (index) => {
        const newArray = dataItem.EpisodeIdentifier.filter((_, i) => i !== index);
        setDataItem({ ...dataItem, EpisodeIdentifier: newArray });
    }

    const handleSubmit = async () => {
        // Validation
        if (!dataItem.Description || dataItem.Description.trim() === '') {
            ToastNotification({ type: TOAST_TYPES.ERROR, title: lang.invalid_error_message_title, message: "Description is required" });
            return;
        }
        if (!dataItem.SeasonFormField) {
            ToastNotification({ type: TOAST_TYPES.ERROR, title: lang.invalid_error_message_title, message: "Season Form Field is required" });
            return;
        }
        if (!dataItem.SeasonIdentifier || dataItem.SeasonIdentifier.length === 0) {
            ToastNotification({ type: TOAST_TYPES.ERROR, title: lang.invalid_error_message_title, message: "Season Identifier is required" });
            return;
        }
        if (!dataItem.EpisodeFormField) {
            ToastNotification({ type: TOAST_TYPES.ERROR, title: lang.invalid_error_message_title, message: "Episode Form Field is required" });
            return;
        }
        if (!dataItem.EpisodeIdentifier || dataItem.EpisodeIdentifier.length === 0) {
            ToastNotification({ type: TOAST_TYPES.ERROR, title: lang.invalid_error_message_title, message: "Episode Identifier is required" });
            return;
        }

        // If "All Channels" is selected, send empty array
        const channelData = dataItem.Channel && dataItem.Channel.some(ch => ch._id === 'all') ? [] :
            dataItem.Channel ? dataItem.Channel.map(ch => ({
                _id: ch._id,
                SID: ch.SID,
                FullChannelName: ch.FullChannelName
            })) : [];

        const saveData = {
            _id: dataItem._id,
            Description: dataItem.Description,
            Channels: channelData,
            SeasonFormField: dataItem.SeasonFormField,
            SeasonIdentifier: dataItem.SeasonIdentifier,
            EpisodeFormField: dataItem.EpisodeFormField,
            EpisodeIdentifier: dataItem.EpisodeIdentifier,
            Archive: dataItem.Archive ?? false,
        }

        console.log(saveData);

        let res = await API.saveData(ENTITYNAME.SeasonEpisodeIdentifierSetting, saveData);
        if (res.success) {
            props.cancelEdit();
            props.refresh();
            utility.deleteLocalStorageItem(ENTITYNAME.SeasonEpisodeIdentifierSetting);
        } else {
            ToastNotification({ type: TOAST_TYPES.ERROR, title: lang.invalid_error_message_title, message: res.message });
        }
    }


    return (
        <>
            <BossDialog
                title={props.item._id > 0 ? props.item.Key : lang.season_episode_identifier_settings_dialog_header}
                onClose={props.cancelEdit}
                width={"400px"}
                height={"max-content"}
            >
                <div className="row">
                    <div className="col-12">
                        <EditPageHeader title={""} onSubmit={handleSubmit} onCancel={props.cancelEdit} showTitle={false} />
                        <div className="row mt-2">
                            <div className="col">
                                <label>{lang.description_label}</label>
                                <input
                                    name={"Description"}
                                    type="text"
                                    className="form-control form-control-sm"
                                    onChange={onChange}
                                    value={dataItem.Description}
                                />
                            </div>
                        </div>
                        <div className="row mt-2">
                            <div className="col">
                                <label>{lang.channels_label}</label>
                                <MultiSelect
                                    style={{ backgroundColor: "white" }}
                                    data={channel}
                                    name="Channels"
                                    textField="FullChannelName"
                                    dataItemKey="_id"
                                    value={dataItem.Channels}
                                    onChange={onChange}
                                />
                            </div>
                        </div>
                        <div className="row mt-2">
                            <div className="col">
                                <label>{lang.season_form_field_label}</label>
                                <DropDownList
                                    data={formFieldData}
                                    name="SeasonFormField"
                                    textField="label"
                                    dataItemKey="_id"
                                    value={dataItem.SeasonFormField}
                                    onChange={onChange}
                                />
                            </div>
                        </div>
                        <div className="row mt-2">
                            <div className="col">
                                <label>{lang.season_identifier_label}</label>
                                <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    placeholder="Type and press Enter to add"
                                    value={currentInput}
                                    onChange={(e) => setCurrentInput(e.target.value)}
                                    onKeyDown={handleArrayInput}
                                />
                                <div className="mt-2">
                                    {dataItem.SeasonIdentifier && dataItem.SeasonIdentifier.map((item, index) => (
                                        <span key={index} className="badge badge-primary mr-1 mb-1" style={{ padding: '5px 8px', fontSize: '12px' }}>
                                            {item}
                                            <span
                                                className="ml-2"
                                                style={{ cursor: 'pointer', fontWeight: 'bold', fontSize: '14px' }}
                                                onClick={() => removeArrayItem(index)}
                                            > ×</span>
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className="row mt-2">
                            <div className="col">
                                <label>{lang.episode_form_field_label}</label>
                                <DropDownList
                                    data={formFieldData}
                                    name="EpisodeFormField"
                                    textField="label"
                                    dataItemKey="_id"
                                    value={dataItem.EpisodeFormField}
                                    onChange={onChange}
                                />
                            </div>
                        </div>
                        <div className="row mt-2">
                            <div className="col">
                                <label>{lang.episode_identifier_label}</label>
                                <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    placeholder="Type and press Enter to add"
                                    value={currentEpisodeInput}
                                    onChange={(e) => setCurrentEpisodeInput(e.target.value)}
                                    onKeyDown={handleEpisodeArrayInput}
                                />
                                <div className="mt-2">
                                    {dataItem.EpisodeIdentifier && dataItem.EpisodeIdentifier.map((item, index) => (
                                        <span key={index} className="badge badge-primary mr-1 mb-1" style={{ padding: '5px 8px', fontSize: '12px' }}>
                                            {item}
                                            <span
                                                className="ml-2"
                                                style={{ cursor: 'pointer', fontWeight: 'bold', fontSize: '14px' }}
                                                onClick={() => removeEpisodeArrayItem(index)}
                                            > ×</span>
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className="row">
                            <div className="col mt-3">
                                <input
                                    type={"checkbox"} name={"Archive"} style={{ marginTop: "10px" }}
                                    onChange={(e) => onChange(e)}
                                    value={dataItem.Archive}
                                    checked={dataItem.Archive} />
                                <label className="ml-1">{lang.archive}</label>
                            </div>
                        </div>
                    </div>
                </div>

            </BossDialog>
        </>)
}

export default SeasonEpisodeIdentifierSettingEditForm;
